import { useEffect, useState } from "react";
import usePersistedOpen from "../../hooks/usePersistedOpen";
import { Layers, Check, X, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { notifyMergeUnits } from "../../lib/mergeNotice";

function Cluster({ c, onDone }) {
  const [picked, setPicked] = useState(new Set(c.products.map((p) => p.id)));
  const [name, setName] = useState(c.suggested_name);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(new Set());
  const visible = c.products.filter((p) => !hidden.has(p.id));
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const ids = [...picked].filter((id) => !hidden.has(id));

  const archiveOne = async (p) => {
    setHidden((s) => { const n = new Set(s); n.add(p.id); return n; });
    setPicked((s) => { const n = new Set(s); n.delete(p.id); return n; });
    try {
      await api.post("/supplier-products/archive", { product_ids: [p.id], archived: true });
      toast.success(`"${p.display_name || p.description}" archived — moved to ${c.supplier_name}'s archive. History kept.`);
      // if the cluster now has fewer than 2 live products, refresh parent so it disappears
      if (c.products.length - (hidden.size + 1) < 2) onDone();
    } catch (e) {
      toast.error(errorMessage(e));
      setHidden((s) => { const n = new Set(s); n.delete(p.id); return n; });
    }
  };

  const act = async (kind) => {
    if (ids.length < 2) return toast.error("Tick at least two products");
    setBusy(true);
    try {
      if (kind === "merge") {
        const { data } = await api.post("/product-matches/merge", { product_ids: ids, preferred_name: name.trim() || c.suggested_name });
        toast.success(`${data.products} products now shown as "${data.preferred_name}" — remembered for every future invoice.`);
        notifyMergeUnits(data.units);
      } else {
        await api.post("/product-matches/separate", { product_ids: ids });
        toast.success("Kept as separate products.");
      }
      onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  if (visible.length < 2) return null;

  return (
    <li className="rounded-lg border border-violet-200 bg-white p-3 space-y-2" data-testid={`variant-cluster-${c.key}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">{c.supplier_name} · {visible.length} names for what looks like one product</p>
      <ul className="grid gap-1 sm:grid-cols-2">
        {visible.map((p) => (
          <li key={p.id}>
            <div className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${picked.has(p.id) ? "border-violet-300 bg-violet-50" : "border-slate-200 opacity-60"}`}>
              <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} data-testid={`variant-pick-${p.id}`} />
                <span className="font-medium text-slate-900 truncate">{p.display_name || p.description}</span>
                <span className="ml-auto shrink-0 font-mono text-xs text-slate-500">{p.pack_size || ""}{p.latest_price != null ? ` · ${gbp(p.latest_price)}` : ""}</span>
              </label>
              <button type="button" onClick={() => archiveOne(p)} className="p-1 text-slate-300 hover:text-amber-700 rounded hover:bg-amber-50 shrink-0" title="Archive — keep history, hide from matching, buying and this list" aria-label="Archive product" data-testid={`variant-archive-${p.id}`}>
                <Archive className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-600 font-medium">Show as:</span>
        {visible.map((p) => <button type="button" key={p.id} onClick={() => setName(p.display_name || p.description)} className={`rounded-full border px-2 py-0.5 ${name === (p.display_name || p.description) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`variant-name-${p.id}`}>{p.display_name || p.description}</button>)}
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="or type a name" className="h-7 rounded border border-slate-200 px-2 min-w-[180px] flex-1" data-testid={`variant-name-input-${c.key}`} />
      </div>
      <div className="flex gap-2">
        <button onClick={() => act("merge")} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid={`variant-merge-${c.key}`}><Check className="h-3.5 w-3.5" /> Merge {ids.length} under this name</button>
        <button onClick={() => act("separate")} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50" data-testid={`variant-separate-${c.key}`}><X className="h-3.5 w-3.5" /> Different products</button>
      </div>
    </li>
  );
}

export default function VariantGroups({ tick, onChanged }) {
  const [clusters, setClusters] = useState(null);
  const [open, setOpen] = usePersistedOpen("variant-groups", true);
  const { restaurantId } = useAuth();
  useEffect(() => { api.get("/product-matches/variants", { params: { restaurant_id: restaurantId || undefined } }).then((r) => setClusters(r.data)).catch(() => setClusters([])); }, [tick, restaurantId]);
  if (!clusters?.length) return null;
  return (
    <section className="mb-4 rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="variant-groups">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left" data-testid="variant-groups-toggle">
        <Layers className="h-4 w-4 text-violet-700" />
        <span className="text-sm font-semibold text-slate-900">Same product, different names? <span className="text-violet-700">{clusters.length}</span></span>
        <span className="ml-auto text-xs text-slate-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <>
        <p className="mt-1 text-xs text-slate-600">One supplier prints the same item under several descriptions. Pick the ones that are the same, choose the name you want to see, and merge — prices and purchase history are then combined.</p>
        <ul className="mt-3 space-y-2">{clusters.map((c) => <Cluster key={c.key} c={c} onDone={onChanged} />)}</ul>
      </>}
    </section>
  );
}
